import { CheckCircle2, ChevronsDown } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  DISCLAIMER_PARAGRAPHS,
  DISCLAIMER_TITLE,
} from "@/lib/disclaimer-content"

export type DisclaimerOverlayMode = "required" | "read_only"

interface DisclaimerOverlayProps {
  open: boolean
  mode: DisclaimerOverlayMode
  isSubmitting: boolean
  showAcknowledgedState: boolean
  isClosingAcknowledged: boolean
  onOpenChange: (open: boolean) => void
  onContinue: () => void
}

export function DisclaimerOverlay({
  open,
  mode,
  isSubmitting,
  showAcknowledgedState,
  isClosingAcknowledged,
  onOpenChange,
  onContinue,
}: DisclaimerOverlayProps) {
  const companyDetailsText = "Lugarno Partners Pty Ltd ACN 607 035 138"
  const afsLicenceText = "Australian Financial Services Licence No. 508934"
  const isRequired = mode === "required"
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const [canAcknowledge, setCanAcknowledge] = useState(false)
  const [showScrollPrompt, setShowScrollPrompt] = useState(false)
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false)

  const updateFadeVisibility = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const canScroll = scrollHeight - clientHeight > 1

    setShowTopFade(canScroll && scrollTop > 1)
    setShowBottomFade(canScroll && scrollTop + clientHeight < scrollHeight - 1)
    setCanAcknowledge(
      !canScroll || scrollTop + clientHeight >= scrollHeight - 1,
    )
  }, [])

  useEffect(() => {
    if (!open) {
      setShowTopFade(false)
      setShowBottomFade(false)
      setCanAcknowledge(false)
      setHasStartedScrolling(false)
      return
    }

    const frameId = window.requestAnimationFrame(updateFadeVisibility)
    window.addEventListener("resize", updateFadeVisibility)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener("resize", updateFadeVisibility)
    }
  }, [open, updateFadeVisibility])

  useEffect(() => {
    if (!open || !isRequired || canAcknowledge || hasStartedScrolling) {
      setShowScrollPrompt(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowScrollPrompt(true)
    }, 450)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [open, isRequired, canAcknowledge, hasStartedScrolling])

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (container && container.scrollTop > 1) {
      setHasStartedScrolling(true)
    }

    updateFadeVisibility()
  }, [updateFadeVisibility])

  const renderDisclaimerParagraph = (paragraph: string, index: number) => {
    if (index !== 0) {
      return <p key={paragraph}>{paragraph}</p>
    }

    const companyStart = paragraph.indexOf(companyDetailsText)
    const licenceStart = paragraph.indexOf(afsLicenceText)

    if (companyStart === -1 || licenceStart === -1) {
      return <p key={paragraph}>{paragraph}</p>
    }

    const beforeCompany = paragraph.slice(0, companyStart)
    const betweenCompanyAndLicence = paragraph.slice(
      companyStart + companyDetailsText.length,
      licenceStart,
    )
    const afterLicence = paragraph.slice(licenceStart + afsLicenceText.length)

    return (
      <p key={paragraph}>
        {beforeCompany}
        <span className="text-foreground font-medium">
          {companyDetailsText}
        </span>
        {betweenCompanyAndLicence}
        <span className="text-foreground font-medium">{afsLicenceText}</span>
        {afterLicence}
      </p>
    )
  }

  const content = (
    <>
      <div className="relative">
        {showTopFade ? (
          <div className="from-background pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b to-transparent" />
        ) : null}
        {showBottomFade ? (
          <div className="from-background pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t to-transparent" />
        ) : null}
        <AnimatePresence>
          {showScrollPrompt ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
            >
              <div className="via-background/95 absolute h-44 w-[30rem] rounded-full bg-gradient-to-r from-transparent to-transparent blur-3xl" />
              <div className="bg-background/80 absolute h-32 w-[24rem] rounded-full blur-2xl" />
              <div className="bg-background/95 relative inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs shadow-sm backdrop-blur-xs">
                <motion.span
                  animate={{ y: [0, 3, 0] }}
                  transition={{
                    duration: 1.1,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  }}
                >
                  <ChevronsDown className="size-3.5" />
                </motion.span>
                <span className="font-medium">Scroll to continue</span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="text-muted-foreground max-h-[50vh] w-full space-y-4 overflow-y-auto px-1 pb-8 text-sm leading-6"
        >
          {DISCLAIMER_PARAGRAPHS.map(renderDisclaimerParagraph)}
        </div>
      </div>
      {isRequired ? (
        <div>
          <Button
            type="button"
            className="w-full"
            disabled={!canAcknowledge || isSubmitting}
            onClick={onContinue}
          >
            I have read and understand
          </Button>
        </div>
      ) : null}
    </>
  )

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      dismissible={!showAcknowledgedState}
    >
      <DrawerContent className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4 max-h-[85vh] duration-300">
        <div className="mx-auto flex w-full max-w-lg flex-col">
          <DrawerHeader
            className={showAcknowledgedState ? "invisible" : undefined}
            aria-hidden={showAcknowledgedState}
          >
            <DrawerTitle>{DISCLAIMER_TITLE}</DrawerTitle>
          </DrawerHeader>
          <div className="relative min-h-[360px] px-4">
            <motion.div
              animate={
                showAcknowledgedState
                  ? { opacity: 0, y: -6 }
                  : { opacity: 1, y: 0 }
              }
              transition={{ duration: 0.25, ease: "easeOut" }}
              className={`space-y-4 overflow-y-auto ${isRequired ? "pb-4" : ""} ${showAcknowledgedState ? "pointer-events-none" : ""}`}
            >
              {content}
            </motion.div>
            <AnimatePresence>
              {showAcknowledgedState ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 flex -translate-y-6 flex-col items-center justify-center gap-3 text-center"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 4 }}
                    animate={
                      isClosingAcknowledged
                        ? { opacity: 0, scale: 0.96, y: 6 }
                        : { opacity: 1, scale: 1, y: 0 }
                    }
                    exit={{ opacity: 0, scale: 0.96, y: 2 }}
                    transition={{
                      duration: isClosingAcknowledged ? 0.16 : 0.22,
                      ease: isClosingAcknowledged ? "easeIn" : "easeOut",
                      delay: isClosingAcknowledged ? 0.1 : 0.05,
                    }}
                  >
                    <CheckCircle2 className="text-primary size-8" />
                  </motion.div>
                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={
                      isClosingAcknowledged
                        ? { opacity: 0, y: 6 }
                        : { opacity: 1, y: 0 }
                    }
                    exit={{ opacity: 0, y: 4 }}
                    transition={{
                      duration: isClosingAcknowledged ? 0.16 : 0.24,
                      ease: isClosingAcknowledged ? "easeIn" : "easeOut",
                      delay: isClosingAcknowledged ? 0 : 0.16,
                    }}
                    className="text-lg font-semibold tracking-tight"
                  >
                    Thanks for acknowledging
                  </motion.p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
